/* ===== Application State Provider (Auth + Organization Context) ===== */

import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { resolveActiveOrganizationId } from '@zetyar/data-contracts'
import type { AuthUser, Membership, Organization, Profile } from '@zetyar/domain'
import { getPermissionsForRoles } from '@zetyar/domain'
import { createSupabaseOrganizationRepository, getMyProfile, updateOrganizationSettings } from '@zetyar/supabase-client'
import type { User } from '@supabase/supabase-js'
import { getSupabaseClient } from '@/infrastructure/supabase/app'
import { AppContext } from './AppContext'

function toAuthUser(supabaseUser: User): AuthUser {
  return {
    uid: supabaseUser.id,
    email: supabaseUser.email ?? '',
    displayName: (supabaseUser.user_metadata?.display_name as string | undefined) ?? null,
    photoURL: (supabaseUser.user_metadata?.avatar_url as string | undefined) ?? null,
    emailVerified: supabaseUser.email_confirmed_at != null,
  }
}

async function loadProfile(authUser: AuthUser, supabaseUser: User): Promise<Profile | null> {
  const client = getSupabaseClient()
  const profile = await getMyProfile(client, supabaseUser)
  if (!profile) return null
  return { ...profile, displayName: profile.displayName || authUser.displayName || authUser.email }
}

function activeOrganizationStorageKey(personId: string): string {
  return `zetyar.activeOrganization.${personId}`
}

function readStoredActiveOrganizationId(personId: string): string | null {
  try {
    return window.localStorage.getItem(activeOrganizationStorageKey(personId))
  } catch {
    return null
  }
}

function writeStoredActiveOrganizationId(personId: string, organizationId: string | null): void {
  try {
    const key = activeOrganizationStorageKey(personId)
    if (organizationId) {
      window.localStorage.setItem(key, organizationId)
    } else {
      window.localStorage.removeItem(key)
    }
  } catch {
    // Browser storage can be unavailable; Supabase remains the source of truth.
  }
}
export function AppContextProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [memberships, setMemberships] = useState<Membership[]>([])
  const [currentOrgId, setCurrentOrgId] = useState<string | null>(null)
  const [isAuthResolved, setIsAuthResolved] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  const currentOrganization = organizations.find((o) => o.id === currentOrgId) || organizations[0] || null

  const currentMembership =
    memberships.find(
      (m) => m.organizationId === currentOrgId && m.userId === profile?.id,
    ) || memberships[0] || null

  const permissions = profile?.isSuperAdmin
    ? getPermissionsForRoles(['organization_admin'])
    : getPermissionsForRoles(currentMembership?.roles ?? [])

  useEffect(() => {
    let isSubscribed = true
    const client = getSupabaseClient()

    const { data: subscription } = client.auth.onAuthStateChange((_event, session) => {
      void (async () => {
        const supabaseUser = session?.user ?? null
        if (!supabaseUser) {
          if (!isSubscribed) return
          setUser(null)
          setProfile(null)
          setOrganizations([])
          setMemberships([])
          setCurrentOrgId(null)
          setIsAuthResolved(true)
          setIsLoading(false)
          return
        }

        setIsLoading(true)
        const nextUser = toAuthUser(supabaseUser)

        try {
          const nextProfile = await loadProfile(nextUser, supabaseUser)
          if (!isSubscribed) return
          setUser(nextUser)
          setProfile(nextProfile)
        } catch (error) {
          console.error('Error al cargar la persona real desde Supabase:', error)
          if (!isSubscribed) return
          setUser(nextUser)
          setProfile(null)
        } finally {
          if (isSubscribed) {
            setIsAuthResolved(true)
            setIsLoading(false)
          }
        }
      })()
    })

    return () => {
      isSubscribed = false
      subscription.subscription.unsubscribe()
    }
  }, [])

  const refreshOrganizations = useCallback(async () => {
    if (!isAuthResolved) return

    if (!user || !profile) {
      setOrganizations([])
      setMemberships([])
      setCurrentOrgId(null)
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    try {
      const client = getSupabaseClient()
      const organizationRepository = createSupabaseOrganizationRepository(client)
      const organizationMemberships = await organizationRepository.listForPerson({
        authUid: user.uid,
        personId: profile.id,
      })

      const nextOrganizations = organizationMemberships.map((item) => item.organization)
      const nextMemberships = organizationMemberships.map((item) => item.membership)

      setOrganizations(nextOrganizations)
      setMemberships(nextMemberships)
      setCurrentOrgId((current) => {
        const resolved = resolveActiveOrganizationId({
          currentOrganizationId: current,
          storedOrganizationId: readStoredActiveOrganizationId(profile.id),
          organizationIds: nextOrganizations.map((organization) => organization.id),
        })
        writeStoredActiveOrganizationId(profile.id, resolved)
        return resolved
      })
    } catch (error) {
      console.error('Error al cargar organizaciones desde Supabase:', error)
      setOrganizations([])
      setMemberships([])
      setCurrentOrgId(null)
    } finally {
      setIsLoading(false)
    }
  }, [isAuthResolved, profile, user])

  useEffect(() => {
    void refreshOrganizations()
  }, [refreshOrganizations])

  useEffect(() => {
    const handleOrganizationsChanged = () => {
      void refreshOrganizations()
    }
    window.addEventListener('zetyar:organizations-changed', handleOrganizationsChanged)
    return () => window.removeEventListener('zetyar:organizations-changed', handleOrganizationsChanged)
  }, [refreshOrganizations])

  const selectOrganization = (orgId: string) => {
    if (!organizations.some((organization) => organization.id === orgId)) return

    setIsLoading(true)
    setCurrentOrgId(orgId)
    if (profile) {
      writeStoredActiveOrganizationId(profile.id, orgId)
    }
    setTimeout(() => setIsLoading(false), 200)
  }

  const signIn = async (email: string, password?: string): Promise<Profile | null> => {
    setIsLoading(true)

    try {
      const client = getSupabaseClient()
      const { data, error } = await client.auth.signInWithPassword({ email, password: password ?? '' })
      if (error) throw error

      const nextUser = toAuthUser(data.user)
      const nextProfile = await loadProfile(nextUser, data.user)
      setUser(nextUser)
      setProfile(nextProfile)
      return nextProfile
    } catch (error) {
      setIsLoading(false)
      throw error
    }
  }

  const signOut = async () => {
    setUser(null)
    setProfile(null)
    setOrganizations([])
    setMemberships([])
    setCurrentOrgId(null)
    setIsAuthResolved(true)

    const client = getSupabaseClient()
    await client.auth.signOut()
  }

  const updateProfile = async (preferredName: string, phone: string | null, avatarUrl: string | null, rut: string | null) => {
    if (!profile) return

    setIsLoading(true)
    try {
      const client = getSupabaseClient()
      const { error } = await client
        .from('persons')
        .update({ preferred_name: preferredName, phone, avatar_url: avatarUrl, rut })
        .eq('id', profile.id)
      if (error) throw error

      const { error: authError } = await client.auth.updateUser({
        data: { display_name: preferredName, avatar_url: avatarUrl },
      })
      if (authError) throw authError

      setUser((current) =>
        current
          ? {
              ...current,
              displayName: preferredName,
              photoURL: avatarUrl,
            }
          : null,
      )

      setProfile((current) =>
        current
          ? {
              ...current,
              displayName: preferredName,
              phone,
              avatarUrl,
              rut,
            }
          : null,
      )
    } catch (error) {
      console.error('Error al actualizar el perfil:', error)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const updateOrganization = async (
    name: string,
    timezone: string,
    currency: string,
    logoUrl?: string | null,
    features?: Record<string, boolean>,
  ) => {
    if (!currentOrganization || !profile || !user) return

    setIsLoading(true)
    try {
      const locale = currency === 'USD' ? 'en-US' : currency === 'EUR' ? 'es-ES' : 'es-CL'
      const client = getSupabaseClient()
      await updateOrganizationSettings(client, currentOrganization.id, { name, timezone, locale, logoUrl, features })

      const organizationRepository = createSupabaseOrganizationRepository(client)
      const organizationMemberships = await organizationRepository.listForPerson({
        authUid: user.uid,
        personId: profile.id,
      })
      const nextOrganizations = organizationMemberships.map((item) => item.organization)
      const persistedOrganization = nextOrganizations.find((organization) => organization.id === currentOrganization.id)

      if (
        !persistedOrganization ||
        persistedOrganization.name !== name ||
        persistedOrganization.timezone !== timezone ||
        persistedOrganization.locale !== locale ||
        (logoUrl !== undefined && persistedOrganization.logoUrl !== logoUrl) ||
        // A missing flag means the module is enabled, same rule as isModuleEnabled.
        (features !== undefined &&
          Object.entries(features).some(
            ([moduleKey, isEnabled]) => (persistedOrganization.features?.[moduleKey] ?? true) !== isEnabled,
          ))
      ) {
        throw new Error('La configuracion de la organizacion no quedo persistida en Supabase.')
      }

      setOrganizations(nextOrganizations)
      setMemberships(organizationMemberships.map((item) => item.membership))
    } catch (error) {
      console.error('Error al actualizar la organizacion:', error)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <AppContext.Provider
      value={{
        user,
        profile,
        currentOrganization,
        currentMembership,
        organizations,
        permissions,
        isLoading: !isAuthResolved || isLoading,
        selectOrganization,
        refreshOrganizations,
        signIn,
        signOut,
        updateProfile,
        updateOrganization,
      }}
    >
      {children}
    </AppContext.Provider>
  )
}
